import React, { useMemo, useState } from 'react';

import { Nullable } from 'chaika-common-types';

import { OptionType } from '@components/Select';
import { FACTOR, Money } from '@store/models/Money';
import { AntdOptionType, parseOptionTypeToAntdOptionType } from '@utils/makeOptionTypes';
import { translate } from '@chaika/i18n/helper';
import { TableField, TableFieldType } from '@components/Table/CreationMenu';

interface Props {
    field: TableField;
    onChange(params: object): void;
}

interface Data {
    value: any;
    options: AntdOptionType[];
    unValidText: string;
    resetField(): void;
    onBlurInput(event: React.FocusEvent<HTMLInputElement>): void;
    onChangeSwitcher(params: any): void;
}

export function useHandlers({ field, onChange }: Props): Data {
    // `field.value` is treated as the initial value; the parent doesn't drive value changes after mount.
    // Use a `key` on the field if you need to force a reset when navigating between records.
    const [value, setValue] = useState(field.value);
    // Options the user picked via async search — kept separately so we don't mutate or stale-cache
    // the parent's `field.options`. The exposed `options` is the union, recomputed each render,
    // so it shrinks when the parent narrows its default list.
    const [userPickedOptions, setUserPickedOptions] = useState<AntdOptionType[]>([]);
    const [unValidText, setUnValidText] = useState<string>('');

    const options = useMemo(() => {
        const base = field.options ?? [];
        if (userPickedOptions.length === 0) return base;
        const existing = new Set(base.map(o => o.value));
        const additions = userPickedOptions.filter(o => !existing.has(o.value));
        return additions.length === 0 ? base : [...base, ...additions];
    }, [field.options, userPickedOptions]);

    function onChangeSwitcher(params: any): void {
        setUnValidTextAfterChange(field.validationParams?.unValidText);
        switch (field.type) {
            case TableFieldType.Currency:
            case TableFieldType.Select:
            case TableFieldType.AsyncUserSelect:
            case TableFieldType.AsyncLegalEntitySelect: {
                onSelectChange(params);
                break;
            }
            case TableFieldType.AsyncSelect: {
                onAsyncSelectChange(params);
                break;
            }
            case TableFieldType.AsyncMultiSelect: {
                onAsyncMultiSelectChange(params);
                break;
            }
            case TableFieldType.AsyncUserMultiSelect:
            case TableFieldType.AsyncLegalEntityMultiSelect: {
                onIdsChange(params);
                break;
            }
            case TableFieldType.Money: {
                onInputMoneyChange(params);
                break;
            }
            case TableFieldType.TextArea: {
                onTextAreaChange(params);
                break;
            }
            case TableFieldType.Duration:
            case TableFieldType.Input: {
                onInputChange(params);
                break;
            }
            case TableFieldType.Checkbox: {
                onCheckboxToggle();
                break;
            }
            case TableFieldType.SearchMultiSelect: {
                onMultiSelectChange(params);
                break;
            }
            case TableFieldType.DateField: {
                onInputDateChange(params);
                break;
            }
            case TableFieldType.BasePriceEditor:
            case TableFieldType.EhrInsuranceInfo:
            case TableFieldType.CompositePriceEditor:
            case TableFieldType.OrganizationAssignment:
            case TableFieldType.DiscountEditor:
            case TableFieldType.TranslationEditor: {
                onComplexFieldChange(params);
                break;
            }
        }
    }

    function onBlurInput(event: React.FocusEvent<HTMLInputElement>) {
        const value = event.target.value;

        // Sync the controlled value to what the masked input currently shows. Without this, the
        // `value` state stays stale (react-imask never fires React's `onChange` while typing), so on
        // the blur re-render react-imask reconciles the field back to the stale value and wipes it.
        setValue(value);

        onChange({
            [field.fieldName]: value,
        });
    }

    function onSelectChange(params: Nullable<OptionType<string>>): void {
        setValue(params?.value ?? null);
        onChange({
            [field.fieldName]: params?.value ?? null,
        });
    }

    function onAsyncSelectChange(params: Nullable<OptionType<string>>): void {
        setValue(params?.value ?? null);
        if (params) {
            setUserPickedOptions(prev => {
                if (prev.some(o => o.value === params.value)) return prev;
                return [...prev, ...parseOptionTypeToAntdOptionType([params])];
            });
        }

        onChange({
            [field.fieldName]: params?.value ?? null,
        });
    }

    function onMultiSelectChange(params: OptionType<string>[]): void {
        setValue(params?.map(item => item.value));

        onChange({
            [field.fieldName]: params?.map(item => item.value) ?? null,
        });
    }

    function onAsyncMultiSelectChange(params: Nullable<OptionType<string>[]>): void {
        const items = params ?? [];
        const ids = items.map(item => item.value);
        setValue(ids);

        // Persist user-picked options from search so they remain selectable on later opens.
        if (items.length > 0) {
            const parsedToAntd = parseOptionTypeToAntdOptionType(items);
            setUserPickedOptions(prev => {
                const existing = new Set(prev.map(o => o.value));
                const additions = parsedToAntd.filter(o => !existing.has(o.value));
                return additions.length === 0 ? prev : [...prev, ...additions];
            });
        }

        onChange({ [field.fieldName]: ids });
    }

    function onIdsChange(params: string[]): void {
        setValue(params);
        onChange({ [field.fieldName]: params ?? null });
    }

    function onInputMoneyChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const value = parseMoneyStringToNumber(event.target.value);
        setValue(event.target.value);

        onChange({
            [field.fieldName]: new Money(value),
        });
    }

    function onTextAreaChange({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>): void {
        setValue(value);

        onChange({
            [field.fieldName]: value,
        });
    }

    function onInputChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void {
        setValue(value);

        onChange({
            [field.fieldName]: value,
        });
    }

    function onCheckboxToggle(): void {
        setValue(!value);

        onChange({
            [field.fieldName]: !value,
        });
    }

    function onInputDateChange(date: Nullable<Date>): void {
        setValue(date?.toISOString());

        onChange({
            [field.fieldName]: date?.toISOString(),
        });
    }

    function onComplexFieldChange(params: any) {
        setValue(params);

        onChange({ [field.fieldName]: params });
    }

    function resetField() {
        setValue(field.value);
        onChange({ [field.fieldName]: field.value });
    }

    function parseMoneyStringToNumber(moneyString: string): number {
        return (moneyString.includes('.') ? parseFloat(moneyString) : Number(moneyString)) * FACTOR;
    }

    function setUnValidTextAfterChange(text?: string): void {
        setUnValidText(text ?? translate('notValid'));
    }

    return {
        value,
        options,
        unValidText,
        resetField,
        onBlurInput,
        onChangeSwitcher,
    };
}
